import React, { useState, useMemo, useEffect } from 'react'
import styled from 'styled-components/macro';
import { useSelector, useDispatch } from 'react-redux'
import { fetchSidenavPages } from 'redux/sidenavSlice'
import { Spacer } from 'components/atoms';
import { InputIcon, Pagination } from 'components/molecules'
import { PageTableSelect } from 'components/organisms'
import { injectIntl } from 'react-intl'
import { intlType } from 'types'
import PropTypes from 'prop-types'
import uniqBy from 'lodash.uniqby'

const SubwebsPagesTableSelect = ({
  intl, subweb, lang, onAddPage
}) => {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const dispatch = useDispatch()
  const {
    tree, subwebPages: {
      pages: subwebPages, isLoading
    }
  } = useSelector(
    (state) => state.sidenav
  )

  useEffect(
    () => {
      setPage(1)
      if (subweb) {
        dispatch(fetchSidenavPages({ subweb, lang }))
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [subweb, lang]
  )

  const handleSearch = (e) => {
    setSearch(e.target.value)
    setPage(1)
  }

  const noDataText = useMemo(
    () => {
      if (isLoading) return ' '
      return search === ''
        ? 'Sem páginas para adicionar ao menu'
        : `Sem resultados para a pesquisa "${search}"`
    },
    [search, isLoading]
  )

  // only show pages that are not in the sidenav
  const subwebPagesRemaining = useMemo(() => {
    let pagesInTree = []
    if (tree && tree.items && Object.keys(tree.items).length > 1) {
      const { rootId, items } = tree
      const itemsKeys = Object.keys(items)
      const itemsKeysWithoutRoot = itemsKeys.filter(key => key !== rootId)
      const itemsData = itemsKeysWithoutRoot.map(key => items[key].data)
      pagesInTree = itemsData
    }
    return subwebPages.filter(
      page => !pagesInTree.find(
        pageInTree => pageInTree.id === page.id || pageInTree.url === page.slug
      )
    )
  }, [subwebPages, tree])

  const filteredPages = useMemo(() => {
    const searchNormalized = search.toLowerCase().trim()
    const filteredTitles = subwebPagesRemaining.filter(page => {
      const titleNormalized = page.title.toLowerCase().trim()
      return titleNormalized.indexOf(searchNormalized) !== -1
    })
    const filteredSlugs = subwebPagesRemaining.filter(page => {
      const slugNormalized = page.slug.toLowerCase().trim()
      return slugNormalized.indexOf(searchNormalized) !== -1
    })
    // remove duplicates
    const filteredAll = [...filteredTitles, ...filteredSlugs]
    const filteredClean = uniqBy(filteredAll, 'id');
    return filteredClean
  }, [subwebPagesRemaining, search])

  // react-table pages start at 0 while rc-pagination starts at 1
  const tablePage = useMemo(() => page - 1, [page])

  return (
    <TableWrapper>
      <Spacer bottom="xl">
        <InputIcon
          icon="search"
          placeholder={intl.formatMessage({ id: 'page.search' })}
          value={search}
          onChange={handleSearch}
        />
      </Spacer>
      <Spacer bottom="m">
        <div>
          <Pagination
            onChange={(nextPage) => setPage(nextPage)}
            current={page}
            total={filteredPages.length}
          />
        </div>
      </Spacer>
      <PageTableSelect
        pages={filteredPages}
        loading={isLoading}
        noDataText={noDataText}
        onAddPage={onAddPage}
        page={tablePage}
      />
      <Spacer top="m">
        <div>
          <Pagination
            onChange={(nextPage) => setPage(nextPage)}
            current={page}
            total={filteredPages.length}
          />
        </div>
      </Spacer>
    </TableWrapper>
  )
}

const TableWrapper = styled.div``

SubwebsPagesTableSelect.propTypes = {
  intl: intlType.isRequired,
  subweb: PropTypes.string.isRequired,
  onAddPage: PropTypes.func,
  lang: PropTypes.string,
}

SubwebsPagesTableSelect.defaultProps = {
  onAddPage: () => { },
  lang: 'pt'
}

export default injectIntl(SubwebsPagesTableSelect)
